import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TuneIcon from "@mui/icons-material/Tune";
import RefreshIcon from "@mui/icons-material/Refresh";
import VideoCard from "./VideoCard";
import api from "../../../conf/api";

/**
 * Brand-scoped "Use Compilation" picker for Brand Publish Step 2.
 *
 * Deliberately the same shape as NativeVideoPicker — one smart search box,
 * two filters people reach for constantly, everything else behind a
 * "Filters (n)" collapse — because the two sit on adjacent tabs of the same
 * step and behaving differently there is a papercut.
 *
 * Filtering is entirely server-side (MRSSManagementApp/compilation_filters.py),
 * which validates every param, so a half-typed date comes back as a 400 with
 * a readable message instead of a 500.
 */

const EMPTY_FILTERS = {
  search: "",
  wgid: "",
  creator: "",
  credit: "",
  has_wgids: "",
  date_field: "created",
  date_from: "",
  date_to: "",
  duration_min: "",
  duration_max: "",
  sort: "newest",
};

const DATE_FIELD_OPTIONS = [
  { value: "created", label: "Created date" },
  { value: "approved", label: "Approval date" },
  { value: "updated", label: "Last updated" },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "title", label: "Title (A–Z)" },
  { value: "-title", label: "Title (Z–A)" },
  { value: "duration", label: "Shortest first" },
  { value: "-duration", label: "Longest first" },
  { value: "wgids", label: "Most WGIDs first" },
];

const HAS_WGIDS_OPTIONS = [
  { value: "", label: "Any" },
  { value: "true", label: "Has attached WGIDs" },
  { value: "false", label: "No WGIDs attached" },
];

/** Strip defaults and blanks so the URL only carries what is actually set. */
function toParams(filters) {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (value === "" || value == null) return acc;
    if (key === "sort" && value === "newest") return acc;
    if (key === "date_field" && value === "created") return acc;
    acc[key] = value;
    return acc;
  }, {});
}

export default function CompilationPicker({
  brandName,
  status,
  selectedKeys = [],
  onSelect,
  emptyMessage = "No approved compilations for this brand yet — create one from the Compilations page and get it approved first.",
}) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Guards against an older, slower response overwriting a newer one — the
  // broad "no filters" query returns more rows and often resolves last.
  const requestSeq = useRef(0);

  const params = useMemo(
    () => ({ brand: brandName, status, ...toParams(filters) }),
    [brandName, status, filters]
  );
  const paramsKey = JSON.stringify(params);

  const fetchCompilations = useCallback(async (queryParams) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/mrss/compilations/", { params: queryParams });
      if (seq !== requestSeq.current) return;
      const data = res.data || {};
      const list = Array.isArray(data) ? data : data.results || [];
      setItems(list);
      setCount(Array.isArray(data) ? list.length : data.count ?? list.length);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setItems([]);
      setCount(0);
      setError(
        err?.response?.data?.detail ||
          "Couldn't load compilations. Adjust the filters and try again."
      );
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  // Debounced so typing in a text filter doesn't fire a request per keystroke.
  useEffect(() => {
    const parsed = JSON.parse(paramsKey);
    const timer = setTimeout(() => fetchCompilations(parsed), 350);
    return () => clearTimeout(timer);
  }, [paramsKey, fetchCompilations]);

  const setFilter = (name) => (event) =>
    setFilters((prev) => ({ ...prev, [name]: event.target.value }));

  const appliedFilters = toParams(filters);
  const activeFilterCount = Object.keys(appliedFilters).filter((k) => k !== "search").length;
  const hasAnyFilter = Object.keys(appliedFilters).length > 0;

  const textField = (name, label, extra = {}) => (
    <TextField
      size="small"
      fullWidth
      label={label}
      value={filters[name]}
      onChange={setFilter(name)}
      {...extra}
    />
  );

  const selectField = (name, label, options, extra = {}) => (
    <TextField
      select
      size="small"
      fullWidth
      label={label}
      value={filters[name]}
      onChange={setFilter(name)}
      {...extra}
    >
      {options.map((opt) => (
        <MenuItem key={`${name}-${opt.value}`} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Box>
      {/* Primary row: one smart search box plus the filter people reach for
          most (WGID), with everything else behind the collapse. */}
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={6}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search title, WCA id, description, credit, WGID or creator…"
            value={filters.search}
            onChange={setFilter("search")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: filters.search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setFilters((p) => ({ ...p, search: "" }))}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          {textField("wgid", "WGID")}
        </Grid>
        <Grid item xs={6} md={2}>
          {selectField("sort", "Sort", SORT_OPTIONS)}
        </Grid>
        <Grid item xs={12} md={2}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Button
              size="small"
              variant={showAdvanced ? "contained" : "outlined"}
              startIcon={<TuneIcon />}
              onClick={() => setShowAdvanced((v) => !v)}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
            </Button>
            <Tooltip title="Reload">
              <span>
                <IconButton
                  size="small"
                  disabled={loading}
                  onClick={() => fetchCompilations(JSON.parse(paramsKey))}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>

      <Collapse in={showAdvanced} unmountOnExit>
        <Grid container spacing={1} mt={0.25}>
          <Grid item xs={12} sm={6} md={3}>
            {textField("creator", "Creator")}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {textField("credit", "Credit")}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {selectField("has_wgids", "WGIDs", HAS_WGIDS_OPTIONS)}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {selectField("date_field", "Date field", DATE_FIELD_OPTIONS)}
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            {textField("date_from", "From", {
              type: "date",
              InputLabelProps: { shrink: true },
            })}
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            {textField("date_to", "To", {
              type: "date",
              InputLabelProps: { shrink: true },
            })}
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            {textField("duration_min", "Min duration (s)", {
              type: "number",
              inputProps: { min: 0 },
            })}
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            {textField("duration_max", "Max duration (s)", {
              type: "number",
              inputProps: { min: 0 },
            })}
          </Grid>
        </Grid>
      </Collapse>

      <Box display="flex" alignItems="center" gap={1} mt={1} mb={0.5} flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">
          {loading
            ? "Loading…"
            : `${count} approved compilation${count === 1 ? "" : "s"}${
                hasAnyFilter ? " matching" : ""
              }`}
        </Typography>
        {hasAnyFilter && !loading && (
          <Button
            size="small"
            onClick={() => setFilters(EMPTY_FILTERS)}
            sx={{ textTransform: "none", py: 0, minWidth: 0 }}
          >
            Clear filters
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} />
      ) : items.length === 0 ? (
        <Alert severity="info">{hasAnyFilter ? "No compilations match these filters." : emptyMessage}</Alert>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary">
            Click a compilation to add it:
          </Typography>
          <Box sx={{ display: "grid", gap: 1, mt: 0.5, gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))" }}>
            {items.map((c) => {
              const already = selectedKeys.includes(c.unique_key);
              return (
                <Box key={c.unique_key} sx={{ minWidth: 0 }}>
                  <Box
                    onClick={() => !already && onSelect(c)}
                    sx={{
                      position: "relative",
                      cursor: already ? "default" : "pointer",
                      opacity: already ? 0.45 : 1,
                      border: "2px solid",
                      borderColor: already ? "success.main" : "transparent",
                      borderRadius: 1,
                    }}
                  >
                    <VideoCard video={{ ...c, s3_url: c.video_url }} />
                    {/* Reusing a compilation across feeds/brands is allowed —
                        this is a heads-up, same spirit as the WGID
                        duplicate-publish warning further down the step. */}
                    {c.published_feeds_count > 0 && (
                      <Tooltip
                        title={`Already published to ${c.published_feeds_count} feed${
                          c.published_feeds_count !== 1 ? "s" : ""
                        }`}
                      >
                        <Chip
                          size="small"
                          color="warning"
                          variant="filled"
                          label={`Used in ${c.published_feeds_count} feed${
                            c.published_feeds_count !== 1 ? "s" : ""
                          }`}
                          sx={{ position: "absolute", top: 4, right: 4, fontSize: 10, height: 20 }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}
